import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { urlLecturaAdjunto } from "../../services/ticketsService";

export type MediaKind = "image" | "video" | "file";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mediaAttachment: {
      insertMediaAttachment: (attrs: { path: string; kind: MediaKind; name?: string }) => ReturnType;
    };
  }
}

function MediaAttachmentView({ node }: NodeViewProps) {
  const { path, kind, name } = node.attrs as { path: string; kind: MediaKind; name: string | null };
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    urlLecturaAdjunto(path)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return (
    <NodeViewWrapper className="my-2 max-w-full" data-drag-handle>
      {error && (
        <div className="text-sm text-destructive border border-destructive/30 rounded p-2">
          No se pudo cargar el adjunto: {error}
        </div>
      )}
      {!error && !url && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded p-3 w-fit">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando adjunto...
        </div>
      )}
      {url && kind === "image" && (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img src={url} className="max-w-full max-h-[480px] rounded border" />
      )}
      {url && kind === "video" && (
        <video src={url} controls className="max-w-full max-h-[480px] rounded border" />
      )}
      {url && kind === "file" && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download={name ?? undefined}
          className="flex items-center gap-2 text-sm border rounded p-3 w-fit max-w-full hover:bg-muted"
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate">{name ?? "Archivo adjunto"}</span>
        </a>
      )}
    </NodeViewWrapper>
  );
}

// Nodo custom en vez de @tiptap/extension-image: los adjuntos viven en un
// bucket privado, así que lo que persistimos es un `path`, no una URL. La
// URL firmada de lectura se resuelve al vuelo cuando se monta el NodeView.
export const MediaAttachment = Node.create({
  name: "mediaAttachment",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      path: { default: null },
      kind: { default: "image" },
      name: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="media-attachment"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes({ "data-type": "media-attachment" }, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaAttachmentView);
  },

  addCommands() {
    return {
      insertMediaAttachment:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs });
        },
    };
  },
});
